import { useState } from 'react';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import EducatorShadow from '@icon/EducatorPhotoRevamp/EducatorCardThumbnail/EducatorShadow';
import Image from '@base/Image';
import useLevelDisplayInfo from '@api/hooks/profile/useLevelDisplayInfo';
import { PARTNER_EDUCATOR_CONFIG } from '@cont/EducatorProfile/constants';

const EducatorImage = styled(Image)`
  height: 150px;
  width: ${Spacings.SPACING_30B};
  margin: 0 auto;
  object-fit: cover;
`;

const NewEducatorImage = styled(Image)`
  & {
    height: 150px;
    width: auto;
    max-height: 100%;
    margin: 0 auto;
    object-fit: contain;
  }
`;

const FallbackAvatar = styled(Image)`
  object-fit: cover;
`;

const UnacademyLogoTopWrapper = styled.div`
  position: absolute;
  top: 30px;
  height: ${Spacings.SPACING_20B};
  width: ${Spacings.SPACING_40B};
  border-bottom-left-radius: ${Spacings.SPACING_40B};
  border-bottom-right-radius: ${Spacings.SPACING_40B};
  border-top-right-radius: 14px;
  border: ${Spacings.SPACING_1B} solid var(--color-i-gray-26);
  right: 10px;
`;

const UnacademyLogoBottomWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: -12.78px;
  height: 42.94px;
  width: 83.01px;
  border-top-left-radius: 83.01px;
  border-top-right-radius: 83.01px;
  border-bottom-right-radius: 14px;
  border: ${Spacings.SPACING_1B} solid var(--color-i-gray-26);
`;

const EducatorMedalWrapper = styled.div`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  position: absolute;
  bottom: -${Spacings.SPACING_1B};
  background: var(--color-base-1);
  border-radius: 50%;
  right: -${Spacings.SPACING_1B};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ShadowWrapper = styled.div`
  position: absolute;
  bottom: -5px;
`;

const ImageWrapper = styled.div`
  position: absolute;
  bottom: -${Spacings.SPACING_5B};
  width: ${Spacings.SPACING_30B};
  display: flex;
  justify-content: center;
  height: ${Spacings.SPACING_40B};
`;

const FallbackWrapper = styled(ImageWrapper)`
  bottom: 0;
`;

const EducatorPhoto = ({
  levelInfo,
  newThumbnail,
  oldThumbnail,
  fallback,
  isPartnerEducator
}) => {
  const { levelsDisplayInfo = [] } = useLevelDisplayInfo();
  const [isNewImageLoaded, setIsNewImageLoaded] = useState(false);

  const onNewImageLoad = () => {
    setIsNewImageLoaded(true);
  };

  const currentLevelDisplayInfo = isPartnerEducator
    ? PARTNER_EDUCATOR_CONFIG
    : levelsDisplayInfo?.find?.((levelDisplayInfo) => {
        return (
          levelDisplayInfo.levelId ===
          (levelInfo?.current_level_id || levelInfo?.currentLevelId)
        );
      });

  const renderEducatorImage = () => {
    if (newThumbnail)
      return (
        <ImageWrapper>
          <NewEducatorImage
            src={newThumbnail}
            height={150}
            width={120}
            alt="Unacademy"
            layout="intrinsic"
            onLoadingComplete={onNewImageLoad}
          />
        </ImageWrapper>
      );

    if (oldThumbnail)
      return (
        <ImageWrapper>
          <EducatorImage
            src={oldThumbnail}
            alt="Unacademy"
            height={150}
            width={120}
          />
        </ImageWrapper>
      );

    if (fallback)
      return (
        <FallbackWrapper>
          <FallbackAvatar
            src={fallback}
            alt="Unacademy"
            height={Spacings.SPACING_40B}
            width={Spacings.SPACING_30B}
          />
        </FallbackWrapper>
      );
    return null;
  };

  return (
    <>
      <UnacademyLogoTopWrapper />
      <UnacademyLogoBottomWrapper />
      {newThumbnail && isNewImageLoaded && (
        <ShadowWrapper>
          <EducatorShadow />
        </ShadowWrapper>
      )}
      {renderEducatorImage()}
      {currentLevelDisplayInfo && (
        <EducatorMedalWrapper>
          <Image
            src={currentLevelDisplayInfo?.asset.badge}
            alt="badge"
            width={24}
            height={24}
          />
        </EducatorMedalWrapper>
      )}
    </>
  );
};

export default EducatorPhoto;
